// components/loginModal/ConvertView.tsx
import SubmitButton from '@/components/forms/SubmitButton';
import TextInput from '@/components/forms/TextInput';
import { PhoneNumberUtil } from 'google-libphonenumber';
import React, { useRef, useState, useEffect } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import CustomToast from '@/components/CustomToast';
import { RegisterSchema, RegisterValues, VerificationSchema } from '../../../forms/Register';
import { useMutation } from 'react-query';
import PhoneInput from '../../forms/PhoneInput';
import { GuestConvert, ResendVerification, ResendVerificationEmail, ResendWhatsappVerification } from '@/services/api/auth';
import { AnimatePresence, motion } from 'framer-motion';
import * as fbq from '@/services/fbpixel';
import useAuth from '@/hooks/useAuth';
import { VerificationData } from '@/types';
import { useTranslation } from 'next-i18next';
import { useLoginModal } from '@/contexts/LoginModalContext';
import { fetchCart } from 'CartActions';
import { useCartDispatch } from '@/contexts/CartContext';
import Verifications from '../Verifications';

interface ConvertViewProps {
  currentView: string;
  headingNumber: string | null;
  shouldVerify: boolean;
  setShouldVerify: React.Dispatch<React.SetStateAction<boolean>>;
  verificationData: VerificationData | undefined;
  setVerificationData: React.Dispatch<React.SetStateAction<VerificationData | undefined>>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  VerificationError: any;
  setVerificaitonError: React.Dispatch<React.SetStateAction<any>>;
  setVerificationError: any;
  isOpen: boolean;
  setCurrentView: any;
  postLoginCallBack: any;
  setPortLoginCallBack: any;
  productType: any;
  setProductType: any;
  executePostLoginCallback: () => void;
  onButtonClick: (e: React.MouseEvent, action: () => void) => void;
}

const ConvertView = ({
  setIsOpen,
  executePostLoginCallback,
}: ConvertViewProps) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tAlerts } = useTranslation('alerts');
  const { registerPrefill } = useLoginModal();
  const { mutate: mutateUser } = useAuth('none');
  const cartDispatch = useCartDispatch();
  const { locale } = useRouter();
  const userLocale = locale === 'ar' ? 'ar' : 'en';

  // Form state
  const [iso2, setIso2] = useState('IL');
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [seconds, setSeconds] = useState(60);
  const [currentMethod, setCurrentMethod] = useState<'whatsapp' | 'phone' | 'email'>('whatsapp');
  const [showVerification, setShowVerification] = useState(false);
  const formValuesRef = useRef<RegisterValues | null>(null);

  // Mutations
  const convertMutation = useMutation(GuestConvert);
  const resendWhatsappMutation = useMutation(ResendWhatsappVerification);
  const resendPhoneMutation = useMutation(ResendVerification);
  const resendEmailMutation = useMutation(ResendVerificationEmail);

  // Timer for resend
  useEffect(() => {
    if (seconds === 0) return;
    const timer = setInterval(() => setSeconds(s => s - 1), 1000);
    return () => clearInterval(timer);
  }, [seconds]);

  const initialValues = {
    first_name: registerPrefill?.first_name || '',
    last_name: registerPrefill?.last_name || '',
    email: registerPrefill?.email || '',
    phone: registerPrefill?.phone || '',
    password: '',
    country_code: registerPrefill?.country_code || '+971',
  };

  // Send WhatsApp verification code
  const handleSendCode = (phone: { country_code: string; number: string }) => {
    setCurrentMethod('whatsapp');
    resendWhatsappMutation.mutate(
      {
        country_code: phone.country_code,
        number: phone.number,
        locale: userLocale,
      },
      {
        onSuccess() {
          setCodeSent(true);
          setSeconds(60);
          toast.custom((t) => (
            <CustomToast type="success" t={t} message={tAlerts('validation_code')} />
          ), { duration: 4000 });
        },
        onError(error: any) {
          toast.custom((t) => (
            <CustomToast type="error" t={t} message={error.response?.data?.message} />
          ), { duration: 4000 });
        },
      }
    );
  };

  // Resend handlers (identical to Verifications)
  const handleResendWhatsapp = (phone: { country_code: string; number: string }) => {
    setCurrentMethod('whatsapp');
    resendWhatsappMutation.mutate(
      { country_code: phone.country_code, number: phone.number, locale: userLocale },
      {
        onSuccess() { setSeconds(60); toast.success(tAlerts('validation_code')); },
        onError(error:any) { toast.error(error.response?.data?.message); }
      }
    );
  };

  const handleResendPhone = (phone: { country_code: string; number: string }) => {
    setCurrentMethod('phone');
    resendPhoneMutation.mutate(
      { country_code: phone.country_code, number: phone.number },
      {
        onSuccess() { setSeconds(60); toast.success(tAlerts('validation_code')); },
        onError(error:any) { toast.error(error.response?.data?.message); }
      }
    );
  };

  const handleResendEmail = (email: string) => {
    setCurrentMethod('email');
    resendEmailMutation.mutate(
      email,
      {
        onSuccess() { setSeconds(60); toast.success(tAlerts('validation_code')); },
        onError(error:any) { toast.error(error.response?.data?.message); }
      }
    );
  };

  // Submit conversion with code
  const handleSubmit = async (
    values: RegisterValues,
    actions: FormikHelpers<RegisterValues>
  ) => {
    // Validate phone
    const phoneUtil = PhoneNumberUtil.getInstance();
    const parsedNumber = phoneUtil.parseAndKeepRawInput(values.phone, iso2);
    if (!phoneUtil.isValidNumber(parsedNumber)) {
      actions.setFieldError('phone', 'Phone Number is Invalid');
      return;
    }

    handleSendCode({ country_code: values.country_code, number: values.phone });
    // Store values for later use
    formValuesRef.current = values;
    setShowVerification(true);
  };

  const handleVerificationSubmit = (code: string) => {
    if (!formValuesRef.current) return;
    const values = formValuesRef.current;
    const name = [values.first_name, values.last_name].filter(Boolean).join(' ').trim();

    convertMutation.mutate(
      {
        name,
        phone: values.phone,
        country_code: values.country_code,
        password: values.password,
        verification_code: code,
      },
      {
        onSuccess(data) {
          // Handle success (same as before)
          if (data?.user) mutateUser(data.user, false);
          if (data?.token) localStorage.setItem('auth_token', data.token);
          cartDispatch(fetchCart());
          setIsOpen(false);
          executePostLoginCallback?.();
          toast.success(tCommon('accountConverted'));
        },
        onError(error: any) {
          // Handle error (same as before)
          if (error.response?.data?.errors?.verification_code) {
            toast.error(error.response.data.errors.verification_code[0]);
          } else {
            toast.error(error.response?.data?.message || tCommon('unknownError'));
          }
        },
      }
    );
  };

  const handleBackFromVerification = () => {
    setShowVerification(false);
  };

  const ref = useRef(null);

  return (
    <div className="modal-content p-3 lg:p-6 xl:p-8 2xl:p-11 pt-0 lg:pt-0 xl:pt-0 2xl:pt-0">
      <AnimatePresence>
        {showVerification ? (
          <Verifications
            phone={{
              country_code: formValuesRef.current?.country_code || '',
              number: formValuesRef.current?.phone || '',
            }}
            email={formValuesRef.current?.email || ''}

            onPhoneChange={(newPhone) => {
              if (formValuesRef.current) {
                formValuesRef.current.phone = newPhone.number;
                formValuesRef.current.country_code = newPhone.country_code;
              }
            }}

            onEmailChange={(newEmail) => {
              if (formValuesRef.current) {
                formValuesRef.current.email = newEmail;
              }
            }}
            fromRegister={false} // prevents auto‑sending on mount
            setIsOpen={setIsOpen}
            setShouldVerify={() => {}} // not needed
            setVerificationData={() => {}} // not needed
            customSubmitHandler={handleVerificationSubmit}
            onBack={handleBackFromVerification}
            isVerifying={convertMutation.isLoading}
            executePostLoginCallback={executePostLoginCallback}
          />
        ) : (
          <motion.div
          initial={{ opacity: 0, translateX: '10%' }}
          transition={{ type: 'spring', duration: 0.5 }}
          animate={{ opacity: 1, translateX: '0' }}
          exit={{ opacity: 0, translateX: '10%' }}
        >
           <div className="mb-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {tCommon('verifyAccountHeading')}
            </h2>
            <p className="text-sm text-gray-500 leading-relaxed max-w-md mx-auto">
              {tCommon('verifyAccountDescription')}
            </p>
          </div>
          <Formik
            initialValues={initialValues}
            innerRef={ref}
            validationSchema={RegisterSchema} // We'll keep same schema for initial validation
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, setFieldValue, setFieldError }) => (
              <Form>
                {/* Personal info fields (pre-filled) */}
                <div className="col lg:flex flex-row" id="convert-form">
                  <div className="form-group flex flex-col mt-3 w-full">
                    <TextInput
                      label={tCommon('nameLabel')}
                      type="text"
                      name="first_name"
                      placeholder={tCommon('namePlaceholder')}
                      isError={!!errors.first_name && touched.first_name}
                      error={errors.first_name}
                      dir={locale === 'ar' ? 'rtl' : 'ltr'}
                    />
                  </div>
                  <div className="form-group flex flex-col mt-3 w-full lg:mr-4">
                    <TextInput
                      label={tCommon('lastNameLabel')}
                      type="text"
                      name="last_name"
                      placeholder={tCommon('lastNamePlaceholder')}
                      isError={!!errors.last_name && touched.last_name}
                      error={errors.last_name}
                      dir={locale === 'ar' ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>

                <div className="form-group flex flex-col mt-3" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                  <PhoneInput
                    values={values}
                    setFieldError={setFieldError}
                    setFieldValue={setFieldValue}
                    setIso2={setIso2}
                    isError={!!errors.phone && touched.phone}
                    error={errors.phone}
                    label={`${tCommon('phoneLabel')}`}
                  />
                </div>

                <div className="form-group flex flex-col mt-3">
                  <TextInput
                    label={tCommon('emailLabel')}
                    type="email"
                    name="email"
                    placeholder={tCommon('emailPlaceholder')}
                    isError={!!errors.email && touched.email}
                    error={errors.email}
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                  />
                </div>

                <div className="form-group flex flex-col mt-3">
                  <TextInput
                    label={tCommon('passwordLabel')}
                    type="password"
                    name="password"
                    placeholder={tCommon('registerPasswordPlaceholder')}
                    isError={!!errors.password && touched.password}
                    error={errors.password}
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                  />
                </div>
                <SubmitButton
                  text={`${tCommon('submit')}`}
                  isLoading={convertMutation.isLoading || resendWhatsappMutation.isLoading}
                  textType="string"
                />
              </Form>
            )}
          </Formik>
        </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConvertView;